import * as THREE from 'three';
import { COMMON_UNIFORMS, LIGHT_FNS, NOISE_FNS, SKY_FNS, TERRAIN_HEIGHT_FNS, WIND_FNS } from '../shaders/common';
import type { Environment } from './Environment';
import { GRASS_TILE, MAX_CLEARINGS, TERRAIN_CELL, TERRAIN_HALF, TERRAIN_RES, type Terrain } from './Terrain';
import { CUT_SCALE, type GrassCut } from './GrassCut';
import { mulberry32 } from './noise';

export const TRAIL_POINTS = 8;
const TILE_TEX_WIDTH = 64;

export type GrassQuality = {
  /** Multiplier on blade density (1 = high). */
  density: number;
  /** Multiplier on grass view distance (1 = 180 m). */
  distance: number;
  alphaToCoverage: boolean;
};

type LayerKind = 'blade' | 'plume';

type LodSpec = { start: number; end: number; segments: number; billboard: number };

// Ring boundaries in meters (scaled by quality.distance). Density per ring comes from targetDensity().
const BLADE_LODS: LodSpec[] = [
  { start: 0, end: 12, segments: 6, billboard: 0.12 },
  { start: 12, end: 22, segments: 5, billboard: 0.25 },
  { start: 22, end: 36, segments: 4, billboard: 0.4 },
  { start: 36, end: 56, segments: 3, billboard: 0.6 },
  { start: 56, end: 86, segments: 2, billboard: 0.75 },
  { start: 86, end: 130, segments: 1, billboard: 0.85 },
  { start: 130, end: 185, segments: 1, billboard: 0.9 },
  { start: 185, end: 250, segments: 1, billboard: 0.95 },
  { start: 250, end: 330, segments: 1, billboard: 0.95 },
];
const PLUME_LODS: LodSpec[] = [
  { start: 0, end: 22, segments: 4, billboard: 0.35 },
  { start: 22, end: 55, segments: 3, billboard: 0.55 },
  { start: 55, end: 115, segments: 2, billboard: 0.75 },
];
const BLADE_D0 = 64;
const PLUME_D0 = 2.3;
const PLUME_FINGERS = 5;

const bladeDensity = (d0: number, d: number) => {
  const x = Math.max(d - 6, 0) / 12;
  return d0 / (1 + x * x);
};
const plumeDensity = (d0: number, d: number) => {
  const x = Math.max(d - 8, 0) / 18;
  return d0 / (1 + x * x);
};

function bladeGeometry(segments: number): THREE.BufferGeometry {
  const positions: number[] = [];
  for (let i = 0; i < segments; i++) {
    const t = i / segments;
    positions.push(t, -1, 0, t, 1, 0);
  }
  positions.push(1, 0, 0);
  const indices: number[] = [];
  for (let i = 0; i < segments - 1; i++) {
    const a = i * 2;
    indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const last = (segments - 1) * 2;
  indices.push(last, last + 1, segments * 2);
  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.instanceCount = 0;
  return geometry;
}

function plumeGeometry(segments: number, fingers: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  // Stem strip (part 0) ending in a point.
  const stemSegments = Math.max(2, segments);
  for (let i = 0; i < stemSegments; i++) {
    const t = i / stemSegments;
    positions.push(t, -1, 0, t, 1, 0);
  }
  positions.push(1, 0, 0);
  for (let i = 0; i < stemSegments - 1; i++) {
    const a = i * 2;
    indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const lastStem = (stemSegments - 1) * 2;
  indices.push(lastStem, lastStem + 1, stemSegments * 2);
  // Feathery fingers (parts 1..n) as open quad strips.
  const fingerSegments = Math.max(2, segments - 1);
  for (let f = 0; f < fingers; f++) {
    const start = positions.length / 3;
    for (let i = 0; i <= fingerSegments; i++) {
      const t = i / fingerSegments;
      positions.push(t, -1, f + 1, t, 1, f + 1);
    }
    for (let i = 0; i < fingerSegments; i++) {
      const a = start + i * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }
  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.instanceCount = 0;
  return geometry;
}

function createPlumeTexture(): THREE.CanvasTexture {
  const w = 64;
  const h = 256;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  const rng = mulberry32(99);
  ctx.clearRect(0, 0, w, h);
  ctx.lineCap = 'round';
  for (let i = 0; i < 260; i++) {
    const y0 = rng() * h * 0.96;
    const spread = (rng() - 0.5) * 2;
    const x0 = w / 2 + spread * w * 0.08;
    const len = 18 + rng() * 60;
    const out = spread * w * (0.28 + rng() * 0.22);
    const shade = 150 + rng() * 105;
    ctx.strokeStyle = `rgba(${shade},${shade},${shade},${0.3 + rng() * 0.5})`;
    ctx.lineWidth = 0.6 + rng() * 1.1;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.quadraticCurveTo(x0 + out * 0.4, y0 + len * 0.45, x0 + out, y0 + len);
    ctx.stroke();
  }
  // Fade the base where the finger meets the stem and taper the tip.
  const image = ctx.getImageData(0, 0, w, h);
  for (let y = 0; y < h; y++) {
    const v = y / (h - 1);
    const fade = Math.min(1, v / 0.08) * Math.min(1, (1 - v) / 0.25);
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      image.data[i + 3] = Math.round(image.data[i + 3] * fade);
    }
  }
  ctx.putImageData(image, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.anisotropy = 4;
  return texture;
}

const SHARED_VERTEX = /* glsl */ `
${COMMON_UNIFORMS}
${TERRAIN_HEIGHT_FNS}
uniform sampler2D uTileTex;
uniform float uTileSize;
uniform int uGridDim;
uniform float uLodDensity;
uniform float uLodSalt;
uniform float uDensity0;
uniform float uMaxDist;
uniform float uBillboard;
uniform vec3 uPlayerPos;
uniform vec4 uTrail[${TRAIL_POINTS}];
uniform vec4 uClearings[${MAX_CLEARINGS}];
uniform sampler2D uCutTex;
uniform vec4 uCutInfo;
uniform vec4 uShock;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vT;
varying vec3 vAlbedo;
varying float vGust;
varying float vShadow;
varying float vFogAmt;
varying vec3 vFogCol;
${NOISE_FNS}
${WIND_FNS}
${SKY_FNS}
${LIGHT_FNS}

void fogVarying(vec3 wp) {
  vec3 d = wp - cameraPosition;
  float dist = length(d);
  vec3 rd = d / max(dist, 1e-3);
  float b = uFogFalloff;
  float k = rd.y * b;
  float integral = abs(k) < 1e-5 ? dist : (1.0 - exp(-dist * k)) / k;
  float fog = uFogDensity * exp(-max(cameraPosition.y, -40.0) * b) * integral;
  vFogAmt = 1.0 - exp(-fog);
  vFogCol = fogColorFor(rd) + uSunColor * pow(max(dot(rd, uSunDir), 0.0), 5.0) * 0.06;
}

vec2 pushBend(vec2 xz, float ground) {
  vec2 bend = vec2(0.0);
  vec2 away = xz - uPlayerPos.xz;
  float pd = length(away);
  float nearFeet = step(abs(ground - uPlayerPos.y), 2.5);
  bend += away / max(pd, 1e-3) * smoothstep(1.05, 0.1, pd) * 1.35 * nearFeet;
  for (int i = 0; i < ${TRAIL_POINTS}; i++) {
    vec4 tr = uTrail[i];
    vec2 aw = xz - tr.xy;
    float td = length(aw);
    bend += aw / max(td, 1e-3) * smoothstep(0.9, 0.1, td) * tr.z;
  }
  // Expanding ring of wind from a double jump or a spin cut.
  if (uShock.w > 0.001) {
    vec2 sa = xz - uShock.xy;
    float sd = length(sa);
    float band = (sd - uShock.z) / 0.9;
    bend += sa / max(sd, 1e-3) * exp(-band * band) * uShock.w;
  }
  return bend;
}

// Height the blade was cut to (metres above ground), or 99 where the field is whole.
float cutCap(vec2 xz) {
  if (uCutInfo.w < 0.5) return 99.0;
  vec2 uv = (xz - uCutInfo.xy) * uCutInfo.z + 0.5;
  if (uv.x <= 0.002 || uv.y <= 0.002 || uv.x >= 0.998 || uv.y >= 0.998) return 99.0;
  float v = texture2D(uCutTex, uv).r;
  return v > 0.996 ? 99.0 : v * ${CUT_SCALE.toFixed(1)};
}

float clearingScale(vec2 xz, float jitter) {
  float s = 1.0;
  for (int i = 0; i < ${MAX_CLEARINGS}; i++) {
    vec4 c = uClearings[i];
    if (c.z > 0.0) s *= smoothstep(c.z * 0.55, c.z * 1.05, distance(xz, c.xy) + (jitter - 0.5) * c.z * 0.25);
  }
  return s;
}

vec3 fieldAlbedo(vec2 xz, vec4 h, vec4 h2, float patchN, float bigN) {
  float hueN = texture2D(uNoise, xz / 17.0).b;
  vec3 golden = vec3(0.5, 0.29, 0.085);
  vec3 straw = vec3(0.68, 0.48, 0.2);
  vec3 olive = vec3(0.3, 0.25, 0.08);
  vec3 rust = vec3(0.46, 0.2, 0.06);
  vec3 alb = mix(golden, straw, clamp(h2.x * 0.6 + patchN * 0.4, 0.0, 1.0));
  alb = mix(alb, olive, smoothstep(0.6, 0.95, bigN) * 0.6 * h.y);
  alb = mix(alb, rust, smoothstep(0.65, 1.0, hueN) * 0.5 * h2.w);
  return alb;
}
`;

const BLADE_VERTEX = /* glsl */ `
${SHARED_VERTEX}
float targetDensity(float d) { float x = max(d - 6.0, 0.0) / 12.0; return uDensity0 / (1.0 + x * x); }

void main() {
  int perTile = uGridDim * uGridDim;
  int tileIdx = gl_InstanceID / perTile;
  int local = gl_InstanceID - tileIdx * perTile;
  vec4 tile = texelFetch(uTileTex, ivec2(tileIdx % ${TILE_TEX_WIDTH}, tileIdx / ${TILE_TEX_WIDTH}), 0);
  vec2 cell = vec2(float(local % uGridDim), float(local / uGridDim));
  vec2 tileId = floor(tile.xy / uTileSize + 0.5);
  vec4 h = hash42(tileId * 64.0 + cell + uLodSalt);
  vec4 h2 = hash42(h.yx * 173.1 + h.wz * 37.7);
  vec2 xz = tile.xy + (cell + h.xy) * (uTileSize / float(uGridDim));

  float dist = distance(xz, cameraPosition.xz);
  float keep = targetDensity(dist) / uLodDensity;
  float clearS = clearingScale(xz, h.w);
  float farFade = smoothstep(uMaxDist, uMaxDist * 0.8, dist);
  if (h.z > keep || clearS < 0.04 || farFade <= 0.0) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float ground = terrainHeight(xz);
  float patchN = texture2D(uNoise, xz / 37.0).g;
  float bigN = texture2D(uNoise, xz / 211.0).a;
  float bladeH = mix(0.48, 1.15, h.w * 0.5 + h2.z * 0.5) * mix(0.7, 1.22, patchN) * mix(0.85, 1.12, bigN);
  bladeH *= farFade * mix(0.3, 1.0, clearS);
  // A cut blade keeps its lower part: stiffer, blunt-topped, coloured as the stalk it was.
  float fullH = bladeH;
  bladeH = min(bladeH, max(cutCap(xz), 0.03));
  float cutK = bladeH / max(fullH, 1e-3);
  float widthMul = clamp(sqrt(uDensity0 / max(targetDensity(dist), 0.02)) * 0.9, 1.0, 18.0);
  float bladeW = mix(0.0055, 0.011, h2.x) * widthMul;
  float facing = h2.y * 6.2831853;

  float gust = gustAt(xz);
  float along = dot(xz, uWindDir);
  float wave = sin(uTime * 1.25 - along * 0.2 + h.y * 0.8);
  float sway = (wave * 0.1 + sin(uTime * 2.6 + h.x * 6.28) * 0.045) * (0.45 + gust) * uWindStrength;
  float flutter = sin(uTime * (6.0 + h.w * 3.0) + h.x * 20.0) * 0.03 * (0.25 + gust);
  vec2 wdir = normalize(uWindDir + (h2.wx - 0.5) * 0.55);
  float windBend = uWindStrength * (0.16 + 0.7 * gust) + sway;
  vec2 leanDir = vec2(cos(facing + 1.2), sin(facing + 1.2));
  vec2 bend = (wdir * windBend + leanDir * mix(0.05, 0.55, h2.z)) * cutK * sqrt(cutK) + pushBend(xz, ground) * cutK;
  float bendAmt = length(bend);
  vec2 bdir = bend / max(bendAmt, 1e-4);
  float ang = min(bendAmt, 1.45);

  float t = position.x;
  float tFull = t * cutK;
  float side = position.y;
  vec3 bdir3 = vec3(bdir.x, 0.0, bdir.y);
  vec3 p2 = (vec3(0.0, cos(ang), 0.0) + bdir3 * sin(ang)) * bladeH;
  vec3 p1 = vec3(0.0, bladeH * mix(0.62, 0.42, ang / 1.45), 0.0) + bdir3 * bladeH * 0.08;
  vec3 pos = 2.0 * (1.0 - t) * t * p1 + t * t * p2;
  vec3 tangent = normalize(2.0 * (1.0 - t) * p1 + 2.0 * t * (p2 - p1) + vec3(0.0, 1e-4, 0.0));
  pos += bdir3 * flutter * t * t * bladeH;

  vec3 base = vec3(xz.x, ground - 0.05, xz.y);
  vec3 sideDir = vec3(-sin(facing), 0.0, cos(facing));
  vec3 toCam = cameraPosition - base;
  vec3 camSide = normalize(vec3(-toCam.z, 0.0, toCam.x) + vec3(1e-4, 0.0, 0.0));
  sideDir *= sign(dot(sideDir, camSide) + 1e-3);
  sideDir = normalize(mix(sideDir, camSide, uBillboard));
  sideDir = normalize(sideDir - tangent * dot(sideDir, tangent));
  float w = bladeW * (1.0 - pow(tFull, 1.4) * 0.9);

  vec3 wp = base + pos + sideDir * side * w;
  vec3 N = normalize(cross(sideDir, tangent));
  N = normalize(N + sideDir * side * 0.55);

  vWorldPos = wp;
  vNormal = N;
  vT = tFull;
  vGust = gust;
  vAlbedo = fieldAlbedo(xz, h, h2, patchN, bigN);
  float selfShadow = mix(0.28, 1.0, smoothstep(0.05, 0.85, tFull));
  vShadow = cloudShadowAt(wp) * characterShadow(wp) * selfShadow;
  fogVarying(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const PLUME_VERTEX = /* glsl */ `
${SHARED_VERTEX}
varying float vKind;
varying vec2 vUv;
float targetDensity(float d) { float x = max(d - 8.0, 0.0) / 18.0; return uDensity0 / (1.0 + x * x); }

vec3 bezier(vec3 a, vec3 b, vec3 c, float t) { return mix(mix(a, b, t), mix(b, c, t), t); }

void main() {
  int perTile = uGridDim * uGridDim;
  int tileIdx = gl_InstanceID / perTile;
  int local = gl_InstanceID - tileIdx * perTile;
  vec4 tile = texelFetch(uTileTex, ivec2(tileIdx % ${TILE_TEX_WIDTH}, tileIdx / ${TILE_TEX_WIDTH}), 0);
  vec2 cell = vec2(float(local % uGridDim), float(local / uGridDim));
  vec2 tileId = floor(tile.xy / uTileSize + 0.5);
  vec4 h = hash42(tileId * 64.0 + cell + uLodSalt + 511.0);
  vec4 h2 = hash42(h.yx * 173.1 + h.wz * 37.7);
  vec2 xz = tile.xy + (cell + h.xy) * (uTileSize / float(uGridDim));

  float dist = distance(xz, cameraPosition.xz);
  float patchN = texture2D(uNoise, xz / 37.0).g;
  float bigN = texture2D(uNoise, xz / 211.0).a;
  // Plumes gather in drifts rather than spreading evenly.
  float drift = smoothstep(0.25, 0.75, texture2D(uNoise, xz / 61.0 + 0.3).b);
  float keep = targetDensity(dist) / uLodDensity * mix(0.35, 1.35, drift);
  float clearS = clearingScale(xz, h.w);
  float farFade = smoothstep(uMaxDist, uMaxDist * 0.8, dist);
  if (h.z > keep || clearS < 0.2 || farFade <= 0.0) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float ground = terrainHeight(xz);
  float stemH = mix(1.15, 1.85, h.w) * mix(0.82, 1.15, patchN) * farFade;
  // Cut below the head: the head is gone and the stem is a stiff stub.
  float cap = cutCap(xz);
  float headCut = step(cap, stemH * 0.92);
  float fullStem = stemH;
  stemH = min(stemH, max(cap, 0.03));
  float cutK = stemH / max(fullStem, 1e-3);
  if (headCut > 0.5 && position.z > 0.5) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }
  float widthMul = clamp(sqrt(uDensity0 / max(targetDensity(dist), 0.02)) * 0.7, 1.0, 6.0);

  float gust = gustAt(xz);
  float along = dot(xz, uWindDir);
  float wave = sin(uTime * 1.25 - along * 0.2 + h.y * 0.8);
  float sway = (wave * 0.09 + sin(uTime * 1.9 + h.x * 6.28) * 0.06) * (0.5 + gust) * uWindStrength;
  vec2 wdir = normalize(uWindDir + (h2.wx - 0.5) * 0.4);
  float lean = mix(0.02, 0.22, h2.z);
  float facing = h2.y * 6.2831853;
  vec2 bend = (wdir * (uWindStrength * (0.12 + 0.55 * gust) + sway) + vec2(cos(facing), sin(facing)) * lean) * cutK * cutK;
  bend += pushBend(xz, ground) * 0.8 * cutK;
  float bendAmt = length(bend);
  vec2 bdir = bend / max(bendAmt, 1e-4);
  float ang = min(bendAmt, 1.3);
  vec3 bdir3 = vec3(bdir.x, 0.0, bdir.y);
  vec3 wdir3 = vec3(wdir.x, 0.0, wdir.y);

  vec3 p2 = (vec3(0.0, cos(ang), 0.0) + bdir3 * sin(ang)) * stemH;
  vec3 p1 = vec3(0.0, stemH * 0.55, 0.0) + bdir3 * stemH * 0.05;
  vec3 base = vec3(xz.x, ground - 0.05, xz.y);
  vec3 toCam = cameraPosition - base;
  vec3 camSide = normalize(vec3(-toCam.z, 0.0, toCam.x) + vec3(1e-4, 0.0, 0.0));

  float part = position.z;
  float t = position.x;
  float side = position.y;
  vec3 pos;
  vec3 tangent;
  vec3 sideDir;
  float w;
  if (part < 0.5) {
    pos = bezier(vec3(0.0), p1, p2, t);
    tangent = normalize(2.0 * (1.0 - t) * p1 + 2.0 * t * (p2 - p1) + vec3(0.0, 1e-4, 0.0));
    sideDir = normalize(camSide - tangent * dot(camSide, tangent));
    w = 0.0042 * widthMul * (1.0 - 0.55 * t);
    vKind = 0.0;
    vUv = vec2(0.0, t);
  } else {
    float k = part - 1.0;
    vec3 stemT = normalize(p2 - p1);
    float az = facing + k * (6.2831853 / ${PLUME_FINGERS}.0) + (fract(h.x * 13.0 + k * 0.61) - 0.5) * 0.9;
    vec3 spread = vec3(cos(az), 0.0, sin(az));
    float len = mix(0.2, 0.36, fract(h.w * 7.0 + k * 0.37)) * mix(0.85, 1.1, patchN);
    vec3 start = p2 - stemT * (0.03 + k * 0.045);
    float windPull = (0.3 + gust * 0.85) * uWindStrength;
    vec3 dir = normalize(stemT * 0.95 + spread * 0.42 + wdir3 * windPull);
    float flick = sin(uTime * (4.5 + k) + h.x * 30.0) * 0.05 * (0.3 + gust);
    vec3 end = start + dir * len + vec3(0.0, -len * (0.25 + 0.3 * gust), 0.0) + (wdir3 + spread * 0.3) * flick;
    vec3 ctrl = start + dir * len * 0.55;
    pos = bezier(start, ctrl, end, t);
    tangent = normalize(mix(ctrl - start, end - ctrl, t) + vec3(0.0, 1e-4, 0.0));
    vec3 s0 = normalize(cross(tangent, spread) + vec3(1e-4));
    s0 *= sign(dot(s0, camSide) + 1e-3);
    sideDir = normalize(mix(s0, camSide, uBillboard));
    sideDir = normalize(sideDir - tangent * dot(sideDir, tangent));
    w = mix(0.01, 0.024, pow(sin(clamp(t, 0.0, 1.0) * 3.14159), 0.6)) * sqrt(widthMul);
    // Keep plumes from ballooning over the lens when they sway right past the camera.
    w *= smoothstep(0.7, 2.4, distance(base + pos, cameraPosition));
    vKind = 1.0;
    vUv = vec2(side * 0.5 + 0.5, t);
  }

  vec3 wp = base + pos + sideDir * side * w;
  vec3 N = normalize(cross(sideDir, tangent));
  N = normalize(N + sideDir * side * 0.5);
  vWorldPos = wp;
  vNormal = N;
  vT = part < 0.5 ? t * 0.9 : 1.0;
  vGust = gust;
  vAlbedo = fieldAlbedo(xz, h, h2, patchN, bigN);
  vShadow = cloudShadowAt(wp) * characterShadow(wp) * (part < 0.5 ? mix(0.35, 1.0, t) : 1.0);
  fogVarying(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const SHARED_FRAGMENT = /* glsl */ `
${COMMON_UNIFORMS}
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vT;
varying vec3 vAlbedo;
varying float vGust;
varying float vShadow;
varying float vFogAmt;
varying vec3 vFogCol;
${LIGHT_FNS}
`;

const BLADE_FRAGMENT = /* glsl */ `
${SHARED_FRAGMENT}
void main() {
  vec3 N = normalize(vNormal);
  if (!gl_FrontFacing) N = -N;
  N = normalize(N + vec3(0.0, 0.3, 0.0));
  vec3 V = normalize(cameraPosition - vWorldPos);
  vec3 root = vec3(0.045, 0.03, 0.014);
  vec3 alb = mix(root, vAlbedo, smoothstep(0.0, 0.6, vT));
  alb = mix(alb, vAlbedo * 1.12 + vec3(0.03, 0.022, 0.01), smoothstep(0.6, 1.0, vT));
  alb *= 1.0 + vGust * 0.16;
  float ao = mix(0.12, 1.0, smoothstep(0.0, 0.85, vT));
  vec3 col = foliageLight(alb, N, V, ao, 0.95, vShadow, 0.07 + vGust * 0.12);
  col = mix(col, vFogCol, vFogAmt);
  gl_FragColor = vec4(col, 1.0);
}
`;

const PLUME_FRAGMENT = /* glsl */ `
${SHARED_FRAGMENT}
uniform sampler2D uPlumeTex;
varying float vKind;
varying vec2 vUv;
void main() {
  vec3 N = normalize(vNormal);
  if (!gl_FrontFacing) N = -N;
  vec3 V = normalize(cameraPosition - vWorldPos);
  float alpha = 1.0;
  vec3 alb;
  float trans;
  float ao;
  if (vKind > 0.5) {
    vec4 tex = texture2D(uPlumeTex, vUv);
    alpha = tex.a;
    #ifndef ALPHA_TO_COVERAGE
    if (alpha < 0.45) discard;
    #else
    if (alpha < 0.02) discard;
    #endif
    alb = mix(vec3(0.44, 0.32, 0.16), vec3(0.76, 0.62, 0.4), tex.r * 0.75 + vUv.y * 0.25);
    N = normalize(N + vec3(0.0, 0.6, 0.0));
    trans = 1.2;
    ao = 0.85;
  } else {
    alb = mix(vec3(0.08, 0.055, 0.025), vAlbedo * 1.05, smoothstep(0.0, 0.6, vT));
    trans = 0.7;
    ao = mix(0.3, 1.0, vT);
  }
  vec3 col = foliageLight(alb, N, V, ao, trans, vShadow, 0.1);
  if (vKind > 0.5) {
    // Fine fibres scatter light almost neutrally, so thin fringes glow pale against the sun.
    float vdl = clamp(dot(-V, uSunDir), 0.0, 1.0);
    vec3 glowTint = mix(vec3(1.0), uTransTint, 0.55);
    col += uSunColor * glowTint * vShadow * (0.035 + 0.25 * vdl * vdl) * (0.3 + 0.9 * (1.0 - alpha));
  }
  col = mix(col, vFogCol, vFogAmt);
  gl_FragColor = vec4(col, alpha);
}
`;

class GrassLayer {
  readonly mesh: THREE.Mesh;
  readonly tileData: Float32Array;
  readonly tileTexture: THREE.DataTexture;
  readonly maxTiles: number;
  readonly perTile: number;
  count = 0;

  constructor(
    readonly spec: LodSpec,
    readonly kind: LayerKind,
    shared: Record<string, THREE.IUniform>,
    gridDim: number,
    salt: number,
    density0: number,
    maxDist: number,
    plumeTex: THREE.Texture | null,
    alphaToCoverage: boolean,
  ) {
    this.perTile = gridDim * gridDim;
    const reach = spec.end + GRASS_TILE * 1.5;
    this.maxTiles = Math.ceil((Math.PI * reach * reach) / (GRASS_TILE * GRASS_TILE)) + 32;
    const rows = Math.ceil(this.maxTiles / TILE_TEX_WIDTH);
    this.tileData = new Float32Array(TILE_TEX_WIDTH * rows * 4);
    this.tileTexture = new THREE.DataTexture(this.tileData, TILE_TEX_WIDTH, rows, THREE.RGBAFormat, THREE.FloatType);
    this.tileTexture.magFilter = THREE.NearestFilter;
    this.tileTexture.minFilter = THREE.NearestFilter;
    this.tileTexture.needsUpdate = true;

    const geometry = kind === 'blade' ? bladeGeometry(spec.segments) : plumeGeometry(spec.segments, PLUME_FINGERS);
    const material = new THREE.ShaderMaterial({
      uniforms: {
        ...shared,
        uTileTex: { value: this.tileTexture },
        uTileSize: { value: GRASS_TILE },
        uGridDim: { value: gridDim },
        uLodDensity: { value: (gridDim * gridDim) / (GRASS_TILE * GRASS_TILE) },
        uLodSalt: { value: salt },
        uDensity0: { value: density0 },
        uMaxDist: { value: maxDist },
        uBillboard: { value: spec.billboard },
        uPlumeTex: { value: plumeTex },
      },
      vertexShader: kind === 'blade' ? BLADE_VERTEX : PLUME_VERTEX,
      fragmentShader: kind === 'blade' ? BLADE_FRAGMENT : PLUME_FRAGMENT,
      side: THREE.DoubleSide,
      alphaToCoverage: kind === 'plume' && alphaToCoverage,
    });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.name = `grass-${kind}-${spec.start}`;
  }

  begin(): void {
    this.count = 0;
  }

  push(x: number, z: number): void {
    if (this.count >= this.maxTiles) return;
    const i = this.count * 4;
    this.tileData[i] = x;
    this.tileData[i + 1] = z;
    this.count++;
  }

  commit(): void {
    this.tileTexture.needsUpdate = true;
    (this.mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = this.count * this.perTile;
    this.mesh.visible = this.count > 0;
  }
}

const sphere = new THREE.Sphere();
const frustum = new THREE.Frustum();
const projScreen = new THREE.Matrix4();

export class Grass {
  readonly group = new THREE.Group();
  readonly trail: THREE.Vector4[] = [];
  private readonly layers: GrassLayer[] = [];
  private readonly bladeLayers: GrassLayer[] = [];
  private readonly plumeLayers: GrassLayer[] = [];
  private readonly shared: Record<string, THREE.IUniform>;
  private readonly plumeTexture = createPlumeTexture();
  private readonly playerPos = new THREE.Vector3();
  private trailTimer = 0;
  private readonly shock = new THREE.Vector4(0, 0, 0, 0);
  private shockSpeed = 0;
  private shockDecay = 0;
  private trailIndex = 0;
  private maxBladeDist = 185;
  private maxPlumeDist = 115;
  visibleBlades = 0;

  constructor(
    env: Environment,
    private readonly terrain: Terrain,
    quality: GrassQuality,
    cut: GrassCut,
  ) {
    for (let i = 0; i < TRAIL_POINTS; i++) this.trail.push(new THREE.Vector4(0, 0, 0, 0));
    this.shared = {
      ...env.uniforms,
      uHeightTex: { value: terrain.heightTexture },
      uHeightInfo: { value: new THREE.Vector3(TERRAIN_HALF, TERRAIN_CELL, TERRAIN_RES) },
      uPlayerPos: { value: this.playerPos },
      uTrail: { value: this.trail },
      uClearings: { value: terrain.clearings },
      uShock: { value: this.shock },
      ...cut.uniforms,
    };
    this.build(quality);
  }

  setQuality(quality: GrassQuality): void {
    for (const layer of this.layers) {
      this.group.remove(layer.mesh);
      layer.mesh.geometry.dispose();
      (layer.mesh.material as THREE.Material).dispose();
      layer.tileTexture.dispose();
    }
    this.layers.length = 0;
    this.bladeLayers.length = 0;
    this.plumeLayers.length = 0;
    this.build(quality);
  }

  private build(quality: GrassQuality): void {
    const tileArea = GRASS_TILE * GRASS_TILE;
    const ds = quality.distance;
    this.maxBladeDist = BLADE_LODS[BLADE_LODS.length - 1].end * ds;
    this.maxPlumeDist = PLUME_LODS[PLUME_LODS.length - 1].end * ds;
    const d0 = BLADE_D0 * quality.density;
    BLADE_LODS.forEach((lod, index) => {
      const spec = { ...lod, start: lod.start * ds, end: lod.end * ds };
      const grid = Math.max(2, Math.ceil(Math.sqrt(bladeDensity(d0, spec.start) * tileArea)));
      const layer = new GrassLayer(spec, 'blade', this.shared, grid, index * 3.7, d0, this.maxBladeDist, null, false);
      this.layers.push(layer);
      this.bladeLayers.push(layer);
      this.group.add(layer.mesh);
    });
    const p0 = PLUME_D0 * Math.min(1.2, 0.5 + quality.density * 0.5);
    PLUME_LODS.forEach((lod, index) => {
      const spec = { ...lod, start: lod.start * ds, end: lod.end * ds };
      const grid = Math.max(2, Math.ceil(Math.sqrt(plumeDensity(p0, spec.start) * tileArea * 1.35)));
      const layer = new GrassLayer(spec, 'plume', this.shared, grid, index * 5.3, p0, this.maxPlumeDist, this.plumeTexture, quality.alphaToCoverage);
      this.layers.push(layer);
      this.plumeLayers.push(layer);
      this.group.add(layer.mesh);
    });
  }

  /** Send an expanding ring of wind through the grass from (x, z). */
  shockwave(x: number, z: number, strength: number, speed: number, decay: number): void {
    this.shock.set(x, z, 0.2, strength);
    this.shockSpeed = speed;
    this.shockDecay = decay;
  }

  update(dt: number, camera: THREE.PerspectiveCamera, player: THREE.Vector3, playerSpeed: number): void {
    this.playerPos.copy(player);
    if (this.shock.w > 0) {
      this.shock.z += this.shockSpeed * dt;
      this.shock.w = Math.max(0, this.shock.w - this.shockDecay * dt);
    }

    // Trampled trail: drop points behind a moving player and let them recover.
    this.trailTimer += dt;
    for (const point of this.trail) point.z = Math.max(0, point.z - dt * 0.55);
    if (playerSpeed > 0.5 && this.trailTimer > 0.18) {
      this.trailTimer = 0;
      const point = this.trail[this.trailIndex];
      point.set(player.x, player.z, 0.85, 0);
      this.trailIndex = (this.trailIndex + 1) % TRAIL_POINTS;
    }

    projScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    frustum.setFromProjectionMatrix(projScreen);
    for (const layer of this.layers) layer.begin();

    const cx = camera.position.x;
    const cz = camera.position.z;
    const reach = this.maxBladeDist;
    const t0x = Math.floor((cx - reach) / GRASS_TILE);
    const t1x = Math.floor((cx + reach) / GRASS_TILE);
    const t0z = Math.floor((cz - reach) / GRASS_TILE);
    const t1z = Math.floor((cz + reach) / GRASS_TILE);
    const half = GRASS_TILE * 0.5;
    let blades = 0;

    for (let tz = t0z; tz <= t1z; tz++) {
      const z0 = tz * GRASS_TILE;
      const dz = Math.max(z0 - cz, 0, cz - (z0 + GRASS_TILE));
      for (let tx = t0x; tx <= t1x; tx++) {
        const x0 = tx * GRASS_TILE;
        const dx = Math.max(x0 - cx, 0, cx - (x0 + GRASS_TILE));
        const d = Math.hypot(dx, dz);
        if (d >= reach) continue;
        if (Math.hypot(x0 + half, z0 + half) > 706) continue;
        const bounds = this.terrain.tileBounds(x0 + half, z0 + half);
        if (!bounds) continue;
        const top = bounds.max + 2.0;
        sphere.center.set(x0 + half, (bounds.min + top) * 0.5, z0 + half);
        sphere.radius = Math.sqrt(half * half * 2 + ((top - bounds.min) * 0.5) ** 2) + 0.6;
        if (!frustum.intersectsSphere(sphere)) continue;
        for (const layer of this.bladeLayers) {
          if (d >= layer.spec.start && d < layer.spec.end) {
            layer.push(x0, z0);
            blades += layer.perTile;
            break;
          }
        }
        if (d < this.maxPlumeDist) {
          for (const layer of this.plumeLayers) {
            if (d >= layer.spec.start && d < layer.spec.end) {
              layer.push(x0, z0);
              break;
            }
          }
        }
      }
    }
    for (const layer of this.layers) layer.commit();
    this.visibleBlades = blades;
  }
}
